/**
 * CustomWidgetChart — render serii (`kind:'series'`) kafla „custom" jako wykres słupkowy / liniowy
 * / kołowy / donut (recharts). Wybór typu po `visualType` przekazanym przez hosta (nie po
 * kształcie danych — ten jest zawsze `series` dla wykresu); brak/nieznany typ → słupki
 * (bezpieczny fallback, patrz `WidgetResultView`).
 *
 * Kolory: seria jednowymiarowa (słupki/linia) używa akcentu motywu (token CSS, jak `ChartWidget`).
 * Kołowy/donut potrzebuje palety KATEGORIALNEJ (wiele odcieni) — bierze ją z tokenów `chart/1–6`
 * systemu „Workflow 3.0" (`lib/chart-colors`), zgodnie z regułą Figmy „never ad-hoc colours".
 */

import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CHART_PALETTE } from '@/lib/chart-colors';
import type { WidgetSeriesItem, WidgetVisualType } from '@/lib/widgets/types';

/** Paleta kategorialna (kołowy/donut) — tokeny `chart/1–6` z systemu (patrz docstring). */
const CATEGORICAL_PALETTE = CHART_PALETTE;

const TOOLTIP_STYLE = {
  fontSize: 12,
  borderRadius: 8,
  border: '1px solid rgb(var(--border-secondary))',
  background: 'rgb(var(--bg-elevated))',
  color: 'rgb(var(--text-primary))',
} as const;

const AXIS_TICK = { fontSize: 11, fill: 'rgb(var(--text-tertiary))' } as const;

export interface CustomWidgetChartProps {
  items: WidgetSeriesItem[];
  visualType: WidgetVisualType;
}

export function CustomWidgetChart({ items, visualType }: CustomWidgetChartProps) {
  // Recharts czyta pola po nazwie — mapujemy raz na stabilny kształt {label, value}.
  const data = useMemo(() => items.map((it) => ({ label: it.label, value: it.value })), [items]);

  const isPieLike = visualType === 'pie' || visualType === 'donut';

  return (
    <div className="relative h-full w-full">
      {/* absolute inset-0 w `relative` rodzicu — recharts mierzy box rodzica, nie odwrotnie:
          rozmiar wykresu nie wpływa na wysokość komórki siatki (brak pętli re-measure). */}
      <div className="absolute inset-0">
        <ResponsiveContainer width="100%" height="100%">
          {visualType === 'line' ? (
            <LineChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border-secondary))" />
              <XAxis dataKey="label" tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={28} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line
                type="monotone"
                dataKey="value"
                stroke="rgb(var(--accent-600))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          ) : isPieLike ? (
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                innerRadius={visualType === 'donut' ? '55%' : 0}
                outerRadius="80%"
                paddingAngle={1}
              >
                {data.map((entry, idx) => (
                  <Cell
                    key={entry.label}
                    fill={CATEGORICAL_PALETTE[idx % CATEGORICAL_PALETTE.length]}
                  />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          ) : (
            // domyślnie i dla 'bar' / nieoczekiwanego snapshotu — słupki
            <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--border-secondary))" />
              <XAxis dataKey="label" tick={AXIS_TICK} />
              <YAxis allowDecimals={false} tick={AXIS_TICK} width={28} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgb(var(--bg-tertiary))' }} />
              <Bar dataKey="value" fill="rgb(var(--accent-600))" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
